import { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { getBase44 } from '@/api/base44Lazy';
import { useToast } from '@/components/ui/use-toast';
import { SERVICES, getService } from '@/lib/pricing';
import { trackLead } from '@/lib/metaPixel';
import { trackGa4Lead, trackAdsConversion } from '@/lib/ga4';
import { reportError } from '@/lib/reportError';
import { markLeadCaptured } from '@/lib/leadCapture';

const CUSTOMER_TYPES = [
  { value: 'landlord', label: 'Landlord' },
  { value: 'agent', label: 'Estate Agent' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'other', label: 'Other' },
];

const inputClass =
  'w-full px-3 py-2.5 border border-navy/15 rounded-lg bg-white text-navy text-sm outline-none focus:border-orange focus:ring-1 focus:ring-orange transition-colors';
const labelClass = 'block text-xs font-semibold text-navy mb-1.5';

export default function CampaignHeroForm() {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    customer_name: '',
    email: '',
    phone: '',
    customer_type: 'landlord',
    postcode: '',
    serviceId: 'inventory_check_in',
  });

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // SDK + auto-reply email load only when the form is actually sent.
      const [base44, { autoReplyHtml, autoReplySubject }] = await Promise.all([getBase44(), import('@/lib/autoReplyEmail')]);
      const service = getService(form.serviceId);
      const payload = {
        customer_name: form.customer_name,
        email: form.email,
        phone: form.phone,
        customer_type: form.customer_type,
        postcode: form.postcode,
        service_ids: [form.serviceId],
        status: 'new',
        is_vip: form.customer_type === 'agent',
      };
      await base44.entities.QuoteRequest.create(payload);
      markLeadCaptured();
      try {
        await base44.functions.invoke('create-lead-card', {
          customer_name: form.customer_name, email: form.email, phone: form.phone,
          postcode: form.postcode, customer_type: form.customer_type, service_name: service?.name,
        });
      } catch (cardErr) { console.error('Lead card sync failed:', cardErr.message); }
      try {
        await base44.functions.invoke('send-notification', {
          to: 'enquiries@daleyinventories.com',
          subject: `New Quick Quote Lead — ${form.customer_name}`,
          body: `New quick quote lead from the inventory offer page.

Name: ${form.customer_name}
Email: ${form.email}
Phone: ${form.phone || 'N/A'}
Type: ${form.customer_type}
Postcode: ${form.postcode || 'N/A'}
Service: ${service?.name || form.serviceId}

View all leads in the admin dashboard.`,
        });
      } catch (emailErr) {
        console.error('Notification email failed:', emailErr.message);
      }
      // Instant branded auto-reply to the enquirer (Day 0 of the follow-up sequence)
      if (form.email) {
        try {
          await base44.functions.invoke('send-notification', {
            to: form.email,
            subject: autoReplySubject,
            html: autoReplyHtml({ name: form.customer_name, service: service?.name }),
            replyTo: 'enquiries@daleyinventories.com',
          });
        } catch (autoReplyErr) {
          console.error('Auto-reply email failed:', autoReplyErr.message);
        }
      }
      trackLead(undefined, 'Quick Quote Lead');
      trackGa4Lead(undefined, 'Quick Quote Lead');
      trackAdsConversion({ email: form.email, phone: form.phone });
      setSubmitted(true);
    } catch (err) {
      await reportError('Quick Quote Form', err, toast);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-7 w-full max-w-md">
      <div className="mb-5">
        <h2 className="font-display font-extrabold text-navy text-2xl leading-tight mb-1">
          Get your instant quote
        </h2>
        <p className="text-sm text-muted-foreground">We'll come back to you the same working day.</p>
      </div>

      {submitted ? (
        <div className="text-center py-6">
          <div className="w-12 h-12 bg-orange rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={24} className="text-navy" />
          </div>
          <h3 className="font-display font-bold text-navy text-lg mb-1">Thanks, {form.customer_name.split(' ')[0] || 'there'}!</h3>
          <p className="text-sm text-muted-foreground">Your quote request is in. We'll be in touch the same working day.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3.5">
          <div>
            <label htmlFor="hf-full-name" className={labelClass}>Full name</label>
            <input id="hf-full-name" type="text" required value={form.customer_name} onChange={set('customer_name')} className={inputClass} placeholder="Jane Smith" />
          </div>
          <div className="grid grid-cols-1 gap-3.5">
            <div>
              <label htmlFor="hf-email" className={labelClass}>Email</label>
              <input id="hf-email" type="email" required value={form.email} onChange={set('email')} className={inputClass} placeholder="you@email.com" />
            </div>
            <div>
              <label htmlFor="hf-phone" className={labelClass}>Phone</label>
              <input id="hf-phone" type="tel" value={form.phone} onChange={set('phone')} className={inputClass} placeholder="07123 456789" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3.5">
            <div>
              <label htmlFor="hf-you-are-a" className={labelClass}>You are a</label>
              <select id="hf-you-are-a" value={form.customer_type} onChange={set('customer_type')} className={inputClass}>
                {CUSTOMER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="hf-postcode" className={labelClass}>Postcode</label>
              <input id="hf-postcode" type="text" value={form.postcode} onChange={set('postcode')} className={inputClass} placeholder="SW1A 1AA" />
            </div>
          </div>
          <div>
            <label htmlFor="hf-service-needed" className={labelClass}>Service needed</label>
            <select id="hf-service-needed" value={form.serviceId} onChange={set('serviceId')} className={inputClass}>
              {SERVICES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <button type="submit" disabled={submitting}
            className="w-full px-6 py-3.5 bg-orange text-navy font-semibold rounded-xl hover:bg-orange-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? 'Sending...' : <>Get my quote <ArrowRight size={18} /></>}
          </button>
        </form>
      )}
    </div>
  );
}